import React from "react";
import {
  AlertTriangle,
  ArrowRightLeft,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Eye,
  Loader2,
  Pause,
  Play,
  Plus,
  RefreshCw,
  TrendingUp,
} from "lucide-react";
import StrategyConfigPanel from "../StrategyConfigPanel";
import Modal from "../Modal";
import { tradingApi, type NQMMPreviousPosition, type NQMMRankingRow, type Position, type StrategyStatus } from "../../services/api";
import NQMMCandidateTable from "./NQMMCandidateTable";
import NQMMHistoryCorrectionModal from "./NQMMHistoryCorrectionModal";
import { usePositionsQuery } from "../../store/tradingApiSlice";
import { getNQMMPortfolioPositions } from "../positionsOrdersUtils";
import {
  formatNQMMDate as formatDate,
  formatNQMMDateOnly as formatDateOnly,
  formatNQMMPercent as formatPercent,
  formatNQMMQuantityChange as formatQuantityChange,
  formatNQMMTimeOnly as formatTimeOnly,
} from "./nqmmStrategyPresentation";

type StrategyKey = "ON" | "ORB" | "DT" | "NQMM";
type ToggleAction = "starting" | "pausing";

interface NQMMStrategyContentProps {
  nqmmStatus: StrategyStatus | null;
  handleStrategyToggle: (strategyName: StrategyKey, currentlyEnabled: boolean | undefined) => Promise<void>;
  strategyToggleLoading: Partial<Record<StrategyKey, ToggleAction>>;
  refreshNQMMStatus: () => Promise<void>;
  rankingRefreshLoading: boolean;
}

const NQMMStrategyContent: React.FC<NQMMStrategyContentProps> = ({
  nqmmStatus,
  handleStrategyToggle,
  strategyToggleLoading,
  refreshNQMMStatus,
  rankingRefreshLoading,
}) => {
  const [showConfigModal, setShowConfigModal] = React.useState(false);
  const [showHistoryModal, setShowHistoryModal] = React.useState(false);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyError, setHistoryError] = React.useState<string | null>(null);
  const [previousPositions, setPreviousPositions] = React.useState<NQMMPreviousPosition[]>([]);
  const [currentPositions, setCurrentPositions] = React.useState<Position[]>([]);
  const [showAllRankings, setShowAllRankings] = React.useState(true);
  const [semiAutomaticSetting, setSemiAutomaticSetting] = React.useState(true);
  const [semiAutomaticSaving, setSemiAutomaticSaving] = React.useState(false);
  const [semiAutomaticError, setSemiAutomaticError] = React.useState<string | null>(null);
  const data = nqmmStatus?.data;
  const { data: portfolioPositions } = usePositionsQuery(undefined, {
    pollingInterval: 10000,
    skipPollingIfUnfocused: true,
  });
  const info = data?.strategy_info || {};
  const rankings = (data?.rankings || []) as NQMMRankingRow[];
  const selectedSymbols = data?.selected_symbols || data?.active_symbols || [];
  const hasPortfolioSnapshot = portfolioPositions?.success === true
    && portfolioPositions.data?.snapshot_available !== false;
  const positions = React.useMemo(() => hasPortfolioSnapshot
    ? getNQMMPortfolioPositions(portfolioPositions?.data?.positions || [])
    : data?.positions || {}, [hasPortfolioSnapshot, portfolioPositions?.data?.positions, data?.positions]);
  const managedSymbols = new Set(
    (data?.managed_symbols ?? Object.keys(data?.target_positions || {})).map((symbol) => symbol.toUpperCase()),
  );
  const manualUnmanagedSymbols = new Set(
    (data?.manual_unmanaged_symbols || []).map((symbol) => symbol.toUpperCase()),
  );
  const previousPositionSymbols = new Set(
    (data?.previous_positions || []).map((position) => position.symbol.toUpperCase()),
  );
  const toggleState = strategyToggleLoading.NQMM;
  const enabled = data?.enabled ?? false;
  const rebalanceStatus = data?.rebalance_status || info.rebalance_status || "NOT_RUN";
  const rankingStatus = data?.ranking_status || "NOT_RUN";
  const displayedRankings = rankings.length > 0
    ? (showAllRankings ? rankings : rankings.slice(0, 25))
    : [];
  const currentPositionRows = Object.entries(positions)
    .map(([symbol, quantity]) => ({ symbol, quantity: Number(quantity) }))
    .filter(({ symbol, quantity }) => (
      Number.isFinite(quantity)
      && quantity !== 0
      && (hasPortfolioSnapshot || (
        !manualUnmanagedSymbols.has(symbol.toUpperCase())
        && (managedSymbols.has(symbol.toUpperCase()) || previousPositionSymbols.has(symbol.toUpperCase()))
      ))
    ))
    .sort((left, right) => left.symbol.localeCompare(right.symbol));
  const buyCandidates = (data?.buy_candidates || []) as NQMMRankingRow[];
  const sellCandidates = (data?.sell_candidates || []) as NQMMRankingRow[];
  const reportedRebalanceCandidates = (data?.rebalance_candidates ?? [
    ...buyCandidates.filter((candidate) => (candidate.rebalance_quantity ?? 0) > 0),
    ...sellCandidates.filter((candidate) => (candidate.rebalance_quantity ?? 0) < 0),
  ]) as NQMMRankingRow[];
  const rebalanceTime = data?.rebalance_time || info.rebalance_time;
  const sleeveAllocation = data?.sleeve_allocation ?? info.sleeve_allocation ?? data?.position_size;
  const cashBuffer = data?.cash_buffer ?? info.cash_buffer;
  const topN = data?.top_n ?? info.top_n;
  const maxPositionAllocation = data?.max_position_allocation ?? info.max_position_allocation;
  const lookbackMonths = data?.lookback_months ?? info.lookback_months;
  const skipRecentMonths = data?.skip_recent_months ?? info.skip_recent_months;
  const tradingDaysPerMonth = data?.trading_days_per_month ?? info.trading_days_per_month;
  const minMomentum = data?.min_momentum ?? info.min_momentum;
  const requirePositiveMomentum = data?.require_positive_momentum ?? info.require_positive_momentum;
  const maxConcurrentHistoryRequests = data?.max_concurrent_history_requests ?? info.max_concurrent_history_requests;
  const semiAutomatic = data?.semi_automatic ?? info.semi_automatic ?? true;

  const positionSizingMethod = data?.position_sizing_method || info.position_sizing_method || "inverse_volatility";
  const sizingLabel = positionSizingMethod === "equal_weight" ? "Equal weight" : "Inverse volatility";
  const qqqAbove200Sma = data?.qqq_above_200_sma ?? info.qqq_above_200_sma;
  const requireQqqAbove200Sma = data?.require_qqq_above_200_sma ?? info.require_qqq_above_200_sma;
  const qqqPrice = data?.qqq_price ?? info.qqq_price;
  const qqq200Sma = data?.qqq_200_sma ?? info.qqq_200_sma;
  const qqqDataAsOf = data?.qqq_data_as_of ?? info.qqq_data_as_of;
  const configChangePending = data?.config_change_pending ?? info.config_change_pending;
  const nextRebalanceAt = data?.next_rebalance_at ?? info.next_rebalance_at;
  const nextRebalanceLabel = `${formatDateOnly(data?.next_rebalance_date || info.next_rebalance_date)} · ${formatTimeOnly(rebalanceTime)}`;
  const qqqRegime = qqqAbove200Sma === true
    ? "Above 200 SMA"
    : qqqAbove200Sma === false
      ? "Below 200 SMA"
      : "Unavailable";
  const rebalanceTargetsReady = Boolean(data?.rebalance_targets_ready);
  const reportedPendingOrders = data?.pending_rebalance_orders
    ?? reportedRebalanceCandidates.filter((candidate) => (candidate.rebalance_quantity ?? 0) !== 0).length;
  const rebalanceWindowOpen = Boolean(
    data?.rebalance_window_open
    || data?.rebalance_due
    || (rebalanceStatus === "PENDING_SEMI_AUTOMATIC" && rebalanceTargetsReady && reportedPendingOrders > 0)
    || (rebalanceStatus === "REBALANCE_DUE")
  );
  const rebalanceCandidates = rebalanceWindowOpen && rebalanceTargetsReady
    ? reportedRebalanceCandidates
    : [];
  const pendingBuyOrders = rebalanceCandidates.filter((c) => (c.rebalance_quantity ?? 0) > 0).length;
  const pendingSellOrders = rebalanceCandidates.filter((c) => (c.rebalance_quantity ?? 0) < 0).length;
  const totalPendingOrders = rebalanceTargetsReady
    ? (data?.pending_rebalance_orders ?? (pendingBuyOrders + pendingSellOrders))
    : 0;

  React.useEffect(() => {
    const configured = data?.semi_automatic ?? info.semi_automatic;
    if (typeof configured === "boolean") {
      setSemiAutomaticSetting(configured);
    }
  }, [data?.semi_automatic, info.semi_automatic]);

  const handleSemiAutomaticToggle = React.useCallback(async () => {
    if (semiAutomaticSaving) return;

    const nextValue = !semiAutomaticSetting;
    const previousValue = semiAutomaticSetting;
    setSemiAutomaticSetting(nextValue);
    setSemiAutomaticSaving(true);
    setSemiAutomaticError(null);

    try {
      const response = await tradingApi.updateStrategyDbConfig("NQMM", {
        config_json: { semi_automatic: nextValue },
      });
      if (!response.success) {
        throw new Error(response.message || "Failed to update NQMM execution mode");
      }
      window.dispatchEvent(new CustomEvent("trading-data-refresh"));
    } catch (error) {
      setSemiAutomaticSetting(previousValue);
      setSemiAutomaticError(
        error instanceof Error ? error.message : "Failed to update NQMM execution mode",
      );
    } finally {
      setSemiAutomaticSaving(false);
    }
  }, [semiAutomaticSaving, semiAutomaticSetting]);

  const statusPositionFallback = React.useMemo<Position[]>(
    () => Object.entries(positions)
      .map(([symbol, quantity]) => ({
        symbol,
        quantity: Number(quantity),
        secType: "STK",
        timestamp: new Date().toISOString(),
      }))
      .filter((position) => Number.isFinite(position.quantity) && position.quantity !== 0),
    [positions],
  );

  const openHistoryModal = React.useCallback(async () => {
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setHistoryError(null);
    const [historyResult, positionsResult] = await Promise.allSettled([
      tradingApi.getNQMMPreviousPositions(),
      tradingApi.getPositions(),
    ]);

    if (historyResult.status === "fulfilled") {
      setPreviousPositions(historyResult.value.data?.previous_positions || []);
    } else {
      setHistoryError(
        historyResult.reason instanceof Error
          ? historyResult.reason.message
          : "Failed to load previous position corrections",
      );
    }

    if (positionsResult.status === "fulfilled") {
      setCurrentPositions(positionsResult.value.data?.positions || []);
    } else {
      // Pausing NQMM must not disable correction history.  If the account
      // endpoint is temporarily unavailable, use the position snapshot that
      // already backs the paused strategy status so existing stock holdings
      // can still be recorded without starting the strategy.
      setCurrentPositions(statusPositionFallback);
      if (statusPositionFallback.length === 0 && historyResult.status === "fulfilled") {
        setHistoryError("Current positions are unavailable while disconnected from the account");
      }
    }

    setHistoryLoading(false);
  }, [statusPositionFallback]);

  const [submittingSymbol, setSubmittingSymbol] = React.useState<string | null>(null);

  const addPreviousPosition = React.useCallback(async (position: Partial<Position> & { symbol: string; quantity: number }) => {
    setSubmittingSymbol(position.symbol);
    setHistoryError(null);
    try {
      const response = await tradingApi.addNQMMPreviousPosition({
        symbol: position.symbol,
        quantity: position.quantity,
        avg_cost: position.avg_cost,
        current_price: position.current_price,
        market_value: position.market_value,
      });
      if (response.success && response.data?.previous_positions) {
        setPreviousPositions(response.data.previous_positions);
        window.dispatchEvent(new CustomEvent("trading-data-refresh"));
      } else if (response.error || response.message) {
        setHistoryError(response.error || response.message || "Failed to save position correction");
      }
    } catch (error) {
      setHistoryError(error instanceof Error ? error.message : "Failed to save position correction");
    } finally {
      setSubmittingSymbol(null);
    }
  }, []);

  const deletePreviousPosition = React.useCallback(async (symbol: string) => {
    setSubmittingSymbol(symbol);
    setHistoryError(null);
    try {
      const response = await tradingApi.deleteNQMMPreviousPosition(symbol);
      if (response.success && response.data?.previous_positions) {
        setPreviousPositions(response.data.previous_positions);
        window.dispatchEvent(new CustomEvent("trading-data-refresh"));
      } else if (response.error || response.message) {
        setHistoryError(response.error || response.message || "Failed to remove position correction");
      }
    } catch (error) {
      setHistoryError(error instanceof Error ? error.message : "Failed to remove position correction");
    } finally {
      setSubmittingSymbol(null);
    }
  }, []);

  if (!data) {
    return (
      <>
        <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 px-4 py-6 text-center">
          <div className="text-sm font-semibold text-amber-200">NQMM status unavailable</div>
          <p className="mt-1 text-xs text-muted">The trading session is offline or has not finished loading. Ranking data will appear when the engine reconnects.</p>
          <button type="button" onClick={openHistoryModal} className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-neon/30 bg-neon/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-neon hover:bg-neon/20">
            <Plus size={14} /> Correct previous 10
          </button>
        </div>
        <NQMMHistoryCorrectionModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          historyLoading={historyLoading}
          historyError={historyError}
          submittingSymbol={submittingSymbol}
          previousPositions={previousPositions}
          currentPositions={currentPositions}
          addPreviousPosition={addPreviousPosition}
          deletePreviousPosition={deletePreviousPosition}
        />
      </>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm min-w-0">
          <span className="text-muted">Strategy</span>
          <span className="font-semibold text-foreground">NQMM</span>
          <span className="text-muted hidden sm:inline">Nasdaq-100 Monthly Momentum</span>
          {data.config_version && (
            <span className="px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-[10px] text-muted font-mono">
              v{data.config_version}
            </span>
          )}
        </div>
        <button
          onClick={() => handleStrategyToggle("NQMM", enabled)}
          disabled={!!toggleState}
          aria-busy={!!toggleState}
          className={`inline-flex min-w-[76px] shrink-0 items-center justify-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-semibold uppercase transition disabled:cursor-wait disabled:opacity-80 ${toggleState
            ? "bg-amber-400/10 text-amber-300 border border-amber-400/20"
            : enabled
              ? "bg-neon-soft text-neon"
              : "text-muted hover:text-foreground hover:bg-white/10"
            }`}
        >
          {toggleState ? <Loader2 size={14} className="animate-spin" /> : enabled ? <Pause size={14} /> : <Play size={14} />}
          {toggleState === "starting" ? "Starting" : toggleState === "pausing" ? "Pausing" : enabled ? "Pause" : "Start"}
        </button>
      </div>

      {rebalanceWindowOpen && (
        <div className="mb-5 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4 text-amber-200 shadow-lg shadow-amber-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 font-bold text-amber-300 text-sm">
                Rebalance Window Open · Action Required
              </div>
              <p className="mt-1 text-xs text-amber-200/90 leading-relaxed">
                {data?.rebalance_warning || `${data.effective_rebalance_frequency || info.effective_rebalance_frequency || data.rebalance_frequency || info.rebalance_frequency || "Monthly"} rebalance is scheduled for today (${formatDateOnly(data?.next_rebalance_date || info.next_rebalance_date)} · ${formatTimeOnly(rebalanceTime)}). ${totalPendingOrders > 0 ? `${totalPendingOrders} order(s) are pending execution to align portfolio with target allocations.` : "Portfolio targets are ready."}`}
              </p>
              {totalPendingOrders > 0 && (
                <div className="mt-2.5 flex flex-wrap items-center gap-2 text-xs font-semibold text-amber-300">
                  <span className="text-amber-200/80">Pending orders:</span>
                  {pendingBuyOrders > 0 && (
                    <span className="px-2 py-0.5 rounded-sm bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-mono text-[11px]">
                      {pendingBuyOrders} BUY
                    </span>
                  )}
                  {pendingSellOrders > 0 && (
                    <span className="px-2 py-0.5 rounded-sm bg-rose-500/20 text-rose-300 border border-rose-500/30 font-mono text-[11px]">
                      {pendingSellOrders} SELL
                    </span>
                  )}
                  <span className="text-amber-200/70 text-[11px]">
                    (Use the order buttons in the rebalance table below to execute)
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-3 mb-5">
        <Metric label="Portfolio sleeve" value={formatPercent(sleeveAllocation, true)} />
        <Metric label="Current allocation" value={formatPercent(data?.current_allocation, true)} />
        <Metric label="Selected stocks" value={`${selectedSymbols.length}/${topN ?? "—"}`} />
        <Metric label="Ranking universe" value={`${data?.universe_size ?? info.universe_size ?? data?.universe?.length ?? "—"}`} />
        <Metric
          label="Next rebalance"
          value={`${nextRebalanceLabel}${rebalanceWindowOpen ? " (Window Open)" : ""}`}
          valueClass={rebalanceWindowOpen ? "text-amber-300 font-bold" : "text-white"}
        />
      </div>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-[11px]">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-neon" />
          <span className="font-bold uppercase tracking-wider text-white/80">QQQ 200-day SMA regime</span>
          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${qqqAbove200Sma === true ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : qqqAbove200Sma === false ? "border-amber-400/30 bg-amber-400/10 text-amber-300" : "border-white/10 bg-white/5 text-muted"}`}>
            {qqqRegime}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-white/60">
          <span>Close {qqqPrice !== undefined && qqqPrice !== null ? `$${Number(qqqPrice).toFixed(2)}` : "—"}</span>
          <span>200 SMA {qqq200Sma !== undefined && qqq200Sma !== null ? `$${Number(qqq200Sma).toFixed(2)}` : "—"}</span>
          <span>{qqqDataAsOf ? `as of ${formatDateOnly(qqqDataAsOf)}` : "data unavailable"}</span>
          <span className={requireQqqAbove200Sma ? "text-neon" : "text-white/40"}>{requireQqqAbove200Sma ? "Hold filter ON" : "Hold filter OFF"}</span>
        </div>
      </div>

      {configChangePending && (
        <div className="mb-5 rounded-xl border border-amber-400/25 bg-amber-400/10 px-4 py-3 text-xs text-amber-200">
          NQMM settings changed. Targets and orders will update at the next scheduled rebalance, using the prior trading session's close.
          {nextRebalanceAt ? <span className="ml-1 font-semibold text-amber-100">Next rebalance: {formatDate(nextRebalanceAt)}.</span> : null}
        </div>
      )}

      <div className="mb-5 rounded-2xl border border-white/10 bg-black/20 overflow-hidden text-[11px]">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-neon" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-white/90">Current positions</h3>
          </div>
          <span className="text-[10px] text-muted uppercase tracking-wider">
            {currentPositionRows.length} open{hasPortfolioSnapshot ? (portfolioPositions?.data?.stale ? " · stale" : "") : " · strategy snapshot"}
          </span>
        </div>
        {currentPositionRows.length === 0 ? (
          <div className="px-4 py-4 text-xs text-muted">
            No current NQMM positions reported. Previous-position corrections remain observational and do not place orders.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-1.5 p-2">
            {currentPositionRows.map(({ symbol, quantity }) => (
              <div key={symbol} className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold tracking-tight text-white">{symbol}</span>
                  {previousPositionSymbols.has(symbol.toUpperCase()) && (
                    <span className="rounded-sm bg-neon/10 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider text-neon">History</span>
                  )}
                </div>
                <div className="mt-0.5 text-[10px] text-muted">{quantity} shares</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.5fr] gap-4 mb-5">
        <div className="min-w-0 p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3">
          <div className="flex flex-col gap-3 border-b border-white/10 pb-3">
            <div className="flex min-w-0 items-center gap-2">
              <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400"><TrendingUp size={16} /></div>
              <h3 className="truncate text-xs font-bold uppercase tracking-wider text-white/90">Strategy information</h3>
            </div>
            <div className="flex w-full flex-wrap items-center gap-1.5">
              {data.config_source && (
                <button
                  type="button"
                  onClick={() => setShowConfigModal(true)}
                  title={data.config_source === "USER" ? "Open saved user configuration" : "Open system default configuration"}
                  className="inline-flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-muted hover:bg-white/10 hover:text-white sm:flex-none"
                >
                  <Eye className="h-3.5 w-3.5 shrink-0" />
                  <span>{data.config_source === "USER" ? "User config" : "Defaults"}</span>
                </button>
              )}
              <button
                type="button"
                onClick={openHistoryModal}
                title="Correct the previous ten NQMM position records"
                className="inline-flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-muted hover:bg-white/10 hover:text-white sm:flex-none"
              >
                <Plus className="h-3.5 w-3.5 shrink-0" />
                <span>Correct 10</span>
              </button>
              <button
                type="button"
                role="switch"
                aria-checked={semiAutomaticSetting}
                aria-busy={semiAutomaticSaving}
                onClick={handleSemiAutomaticToggle}
                disabled={semiAutomaticSaving}
                title={semiAutomaticSetting
                  ? "Semi-automatic: review each NQMM order before submitting"
                  : "Automatic: NQMM may submit rebalance orders on schedule"}
                className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-muted transition hover:bg-white/10 hover:text-white disabled:cursor-wait disabled:opacity-70 sm:flex-none"
              >
                <span className={semiAutomaticSetting ? "text-neon" : "text-amber-300"}>
                  {semiAutomaticSaving ? "Saving…" : semiAutomaticSetting ? "Semi-auto" : "Automatic"}
                </span>
                <span className={`relative h-3.5 w-6 shrink-0 rounded-full transition ${semiAutomaticSetting ? "bg-neon/70" : "bg-white/20"}`}>
                  <span className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white transition ${semiAutomaticSetting ? "left-3" : "left-0.5"}`} />
                </span>
                {semiAutomaticSaving && <Loader2 className="h-3 w-3 shrink-0 animate-spin text-amber-300" />}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-muted leading-relaxed">
            Long-only cross-sectional momentum across the configured Nasdaq-100 universe. It uses a {lookbackMonths ?? "—"}-month lookback,
            skips {skipRecentMonths ?? "—"} recent months, and selects {requirePositiveMomentum ? "positive-momentum " : "eligible "}names using capped {sizingLabel.toLowerCase()}.
          </p>
          <div className="grid grid-cols-1 gap-x-5 gap-y-2 text-[11px] sm:grid-cols-2">
            <InfoRow label="Portfolio sleeve" value={formatPercent(sleeveAllocation, true)} />
            <InfoRow label="Cash buffer" value={formatPercent(cashBuffer, true)} />
            <InfoRow label="Selected stocks" value={`${selectedSymbols.length} / ${topN ?? "—"}`} />
            <InfoRow label="Max per stock" value={formatPercent(maxPositionAllocation, true)} />
            <InfoRow label="Lookback" value={`${lookbackMonths ?? "—"} months`} />
            <InfoRow label="Skip recent" value={skipRecentMonths === 0 ? "None" : `${skipRecentMonths ?? "—"} month${skipRecentMonths === 1 ? "" : "s"}`} />
            <InfoRow label="Trading days / month" value={`${tradingDaysPerMonth ?? "—"}`} />
            <InfoRow label="Sizing" value={sizingLabel} />
            <InfoRow label="Vol window" value={`${data.volatility_lookback_months ?? info.volatility_lookback_months ?? "—"} months`} />
            <InfoRow label="Min momentum" value={formatPercent(minMomentum)} />
            <InfoRow label="Positive momentum" value={requirePositiveMomentum ? "Required" : "Allowed"} />
            <InfoRow label="Schedule" value={`${data.effective_rebalance_frequency || info.effective_rebalance_frequency || data.rebalance_frequency || info.rebalance_frequency || "monthly"} · last working day · ${formatTimeOnly(rebalanceTime)}${rebalanceTime?.startsWith("15:00") ? " · 1h before close" : ""}`} />
            <InfoRow label="QQQ regime" value={`${qqqRegime}${data.qqq_price && data.qqq_200_sma ? ` · $${Number(data.qqq_price).toFixed(2)} / $${Number(data.qqq_200_sma).toFixed(2)}` : ""}`} valueClass={qqqAbove200Sma === false ? "text-amber-300" : qqqAbove200Sma === true ? "text-emerald-300" : "text-muted"} />
            <InfoRow label="QQQ hold filter" value={requireQqqAbove200Sma ? "Only above 200 SMA" : "Off"} />
            <InfoRow label="Rebalance cadence" value="Month end only" />
            <InfoRow label="Execution" value={semiAutomatic ? "Semi-auto · order buttons" : "Automatic"} valueClass={semiAutomatic ? "text-neon" : "text-amber-300"} />
            <InfoRow label="Next action" value={nextRebalanceLabel} valueClass={configChangePending ? "text-amber-300" : "text-white/80"} />
            <InfoRow label="History requests" value={`${maxConcurrentHistoryRequests ?? "—"} at a time`} />
            <InfoRow label="Last run" value={formatDate(data.last_rebalance_date || info.last_rebalance_date)} />
            <InfoRow label="Rebalance status" value={rebalanceStatus} valueClass={rebalanceStatus === "ERROR" ? "text-rose-300" : "text-emerald-300"} />
            <InfoRow label="Momentum data" value={rankingStatus} valueClass={rankingStatus === "ERROR" ? "text-rose-300" : rankingStatus === "RUNNING" ? "text-amber-300" : "text-emerald-300"} />
            <InfoRow label="Momentum close" value={data.ranking_data_as_of ? formatDateOnly(data.ranking_data_as_of) : formatDate(data.last_ranking_at)} />
            <InfoRow label="Cash buffer" value={formatPercent(data.cash_buffer, true)} />
          </div>
          {data.rebalance_error && (
            <div className="rounded-lg border border-rose-400/20 bg-rose-400/5 px-3 py-2 text-[11px] text-rose-300">
              {data.rebalance_error}
            </div>
          )}
          {semiAutomaticError && (
            <div className="rounded-lg border border-rose-400/20 bg-rose-400/5 px-3 py-2 text-[11px] text-rose-300">
              {semiAutomaticError}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CalendarClock size={16} className="text-neon" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-white/90">Nasdaq momentum</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted uppercase tracking-wider">
                {showAllRankings ? `${rankings.length} stocks` : `Top ${data.top_n ?? info.top_n ?? "—"}`}
              </span>
              <button
                type="button"
                onClick={refreshNQMMStatus}
                disabled={rankingRefreshLoading}
                aria-busy={rankingRefreshLoading}
                aria-label="Refresh Nasdaq momentum rankings"
                title="Reload the latest Nasdaq momentum snapshot"
                className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted hover:bg-white/10 hover:text-white disabled:cursor-wait disabled:opacity-60"
              >
                <RefreshCw size={13} className={rankingRefreshLoading ? "animate-spin" : ""} />
                <span className="hidden sm:inline">{rankingRefreshLoading ? "Refreshing" : "Refresh"}</span>
              </button>
              {rankings.length > 25 && (
                <button
                  type="button"
                  onClick={() => setShowAllRankings((current) => !current)}
                  aria-expanded={showAllRankings}
                  className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted hover:bg-white/10 hover:text-white"
                >
                  {showAllRankings ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  {showAllRankings ? "Collapse" : "Show all"}
                </button>
              )}
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto ux-scrollbar">
            {displayedRankings.length === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-muted">
                {rankingStatus === "RUNNING"
                  ? "Refreshing completed daily momentum data…"
                  : data.ranking_error || (enabled ? "Waiting for the daily momentum snapshot." : "No ranking snapshot yet — the shared history backfill may still be running.")}
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#111827] text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="px-4 py-2 text-left">Rank</th>
                    <th className="px-4 py-2 text-left">Symbol</th>
                    <th className="px-4 py-2 text-right">Momentum</th>
                    <th className="px-4 py-2 text-right">Volatility</th>
                    <th className="px-4 py-2 text-right" title="Indicative targets based on the current ranking; actual trades use approved rebalance targets">Indicative %</th>
                    <th className="px-4 py-2 text-right">Indicative shares</th>
                    <th className="px-4 py-2 text-right">Position shares</th>
                    <th className="px-4 py-2 text-right">Indicative change</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRankings.map((row) => {
                    const quantity = hasPortfolioSnapshot
                      ? positions[row.symbol.toUpperCase()] ?? 0
                      : row.position?.shares ?? (managedSymbols.has(row.symbol.toUpperCase()) ? positions[row.symbol] : 0) ?? 0;
                    const targetQuantity = row.indicative_target_quantity ?? row.target_quantity ?? data.target_positions?.[row.symbol] ?? 0;
                    const quantityChange = Number(targetQuantity) - Number(quantity);
                    return (
                      <tr key={row.symbol} className={`${row.selected ? "bg-emerald-400/10" : ""} border-t border-white/5`}>
                        <td className="px-4 py-2 font-mono text-muted">{row.rank ?? "—"}</td>
                        <td className="px-4 py-2 font-bold text-white">{row.symbol}</td>
                        <td className={`px-4 py-2 text-right font-mono ${Number(row.momentum) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                          {row.ranking_status === "INSUFFICIENT_HISTORY" ? "No history" : formatPercent(row.momentum)}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-white/70">{formatPercent(row.realized_volatility)}</td>
                        <td className="px-4 py-2 text-right font-mono text-white/70">{formatPercent(row.indicative_target_allocation ?? row.target_allocation)}</td>
                        <td className="px-4 py-2 text-right font-mono text-white/70">{targetQuantity}</td>
                        <td className="px-4 py-2 text-right font-mono text-white/70">{quantity}</td>
                        <td className={`px-4 py-2 text-right font-mono ${quantityChange > 0 ? "text-emerald-300" : quantityChange < 0 ? "text-rose-300" : "text-white/50"}`}>
                          {formatQuantityChange(quantityChange)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div className="mb-5">
        <NQMMCandidateTable
          title="Rebalance to target"
          icon={<ArrowRightLeft size={16} className="text-neon" />}
          candidates={rebalanceCandidates}
          rebalanceWindowOpen={rebalanceWindowOpen && rebalanceTargetsReady}
        />
      </div>

      {showConfigModal && (
        <Modal isOpen={showConfigModal} onClose={() => setShowConfigModal(false)} title="NQMM Strategy Setup" width="max-w-5xl">
          <StrategyConfigPanel strategyName="NQMM" readOnly />
        </Modal>
      )}

      <NQMMHistoryCorrectionModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        historyLoading={historyLoading}
        historyError={historyError}
        submittingSymbol={submittingSymbol}
        previousPositions={previousPositions}
        currentPositions={currentPositions}
        addPreviousPosition={addPreviousPosition}
        deletePreviousPosition={deletePreviousPosition}
      />
    </>
  );
};

const Metric = ({ label, value, valueClass = "text-white" }: { label: string; value: string; valueClass?: string }) => (
  <div className="p-3 rounded-xl bg-white/5 border border-white/10 min-w-0">
    <div className="text-[10px] uppercase tracking-wider font-bold text-muted mb-1">{label}</div>
    <div className={`text-sm font-semibold truncate ${valueClass}`} title={value}>{value}</div>
  </div>
);

const InfoRow = ({ label, value, valueClass = "text-white/80" }: { label: string; value: string; valueClass?: string }) => (
  <div className="flex min-w-0 items-center justify-between gap-2 border-b border-white/5 py-1.5">
    <span className="min-w-0 truncate text-muted">{label}</span>
    <span className={`min-w-0 truncate font-mono text-right ${valueClass}`} title={value}>{value}</span>
  </div>
);

export default NQMMStrategyContent;
